import { motion } from "framer-motion";
import { Star } from "lucide-react";

const chapters = [
  {
    num: "01",
    title: "Craftsmanship Over Shortcuts",
    body: "Every coat, every grain, every fiber gets the full measure of care. We prep longer, finish cleaner, and never leave until it looks like we were never there — except for the result.",
  },
  {
    num: "02",
    title: "Two Decades of San Angelo Trust",
    body: "Since 2004, San Angelo neighbors have handed us their keys and their biggest investments. Most of our work comes from referrals — the only advertising that can't be bought.",
  },
  {
    num: "03",
    title: "The Flawless Finish Guarantee",
    body: "Fully insured, free quotes, straight talk, and a finish we stand behind. If a line isn't crisp or a floor doesn't shine, we make it right. That's been the deal for over 20 years.",
  },
];

const stats = [
  { value: "2004", label: "Founded" },
  { value: "22+", label: "Years Experience" },
  { value: "1,400+", label: "Projects Completed" },
  { value: "100%", label: "Free Quotes" },
];

const ease = [0.16, 1, 0.3, 1];

export default function Heritage() {
  return (
    <section
      id="heritage"
      data-testid="heritage-section"
      className="relative bg-zinc-950 text-white py-20 lg:py-32 grain-overlay overflow-hidden"
    >
      <div className="px-4 sm:px-8 lg:px-12 max-w-7xl mx-auto relative z-10">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, ease }}
          className="font-mono-x text-xs uppercase tracking-[0.3em] text-red-500 font-semibold mb-4"
        >
          Our Heritage
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease }}
          className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl max-w-2xl mb-16 lg:mb-24"
        >
          The manifesto behind every brushstroke.
        </motion.h2>

        <div className="flex flex-col">
          {chapters.map((c, i) => (
            <motion.div
              key={c.num}
              data-testid={`heritage-chapter-${c.num}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.08, ease }}
              className="group grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8 py-10 border-t border-zinc-800 last:border-b"
            >
              <div className="md:col-span-2">
                <Star size={16} className="text-red-500" fill="currentColor" />
              </div>
              <h3 className="md:col-span-4 font-display font-bold tracking-tight text-2xl sm:text-3xl group-hover:text-red-500 transition-colors duration-300">
                {c.title}
              </h3>
              <p className="md:col-span-6 text-zinc-400 leading-relaxed text-base">
                {c.body}
              </p>
            </motion.div>
          ))}
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-800 border border-zinc-800 mt-16 lg:mt-24">
          {stats.map((s, i) => (
            <motion.div
              key={s.label}
              data-testid={`stat-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.1, ease }}
              className="bg-zinc-950 p-8 lg:p-10"
            >
              <div className="font-display font-black text-4xl lg:text-5xl text-white tracking-tight">
                {s.value}
              </div>
              <div className="mt-2 font-mono-x text-[10px] uppercase tracking-[0.25em] text-red-500 font-semibold">
                {s.label}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
